import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ManagerRolesArray, RolesValues } from '@constants';
import { getRequestFoundCandidatesCount, RequestItem, useOptionsApi, useProfileAPI } from '@innowise-group/core';
import { Input, Select } from '@innowise-group/mui-kit';
import { DatePicker, LabeledCheckbox } from '@shared-mui-components';

import { RequestFormViewProps } from '../../request-form.types';
import { FormSection } from '../form-section';
import * as Styled from './general-section.styles';

type GeneralSectionProps = Pick<
  RequestFormViewProps,
  'isEdit' | 'disabled' | 'requestCustomerOptions' | 'searchRequestCustomerOptions' | 'currentRequestEdition'
>;

const GeneralSection: React.FC<GeneralSectionProps> = ({
  isEdit,
  disabled,
  requestCustomerOptions,
  searchRequestCustomerOptions,
  currentRequestEdition,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileAPI();
  const { professionOptions, domainOptions, unitOptions, divisionOptions, priorityOptions } = useOptionsApi();
  const { control } = useFormContext<RequestItem>();

  const isCustomerDisabled = [RolesValues.Supervisor, ...ManagerRolesArray].includes(profile?.role?.name);

  const billableOptions = useMemo(
    () => [
      { title: t('buttons.yes'), value: 'true' },
      { title: t('buttons.no'), value: 'false' },
    ],
    [t],
  );

  return (
    <FormSection title={t('pages.requests.creationModal.generalInformation')}>
      <Styled.NameCell>
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              fullWidth
              required
              value={value}
              inputRef={ref}
              onChange={onChange}
              label={t('pages.requests.creationModal.name')}
              placeholder={t('pages.requests.creationModal.enterName')}
              error={!!errors?.name?.message}
              helperText={errors?.name?.message}
            />
          )}
        />
      </Styled.NameCell>
      <Controller
        name="customers"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            disableClearable={!value?.length}
            options={requestCustomerOptions}
            disabled={isCustomerDisabled}
            fullWidth
            multiple
            disableCloseOnSelect
            limitTags={1}
            renderInput={(params) => (
              <Input
                {...params}
                fullWidth
                required
                onChange={searchRequestCustomerOptions}
                label={t('pages.requests.creationModal.hiringManager')}
                placeholder={t('pages.candidates.statuses.selectAColleague')}
                error={!!errors?.customers?.message}
                helperText={errors?.customers?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="isBillable"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={billableOptions}
            // US-10.3 AC-3: Billable is not edited after the request is created
            disabled={isEdit}
            renderInput={(params) => (
              <Input
                {...params}
                fullWidth
                required
                label={t('pages.requests.creationModal.billable')}
                error={!!errors?.isBillable?.message}
                helperText={errors?.isBillable?.message?.toString()}
              />
            )}
          />
        )}
      />
      {/* US-10.1 «Юнит»: an HRM org unit of type 6 (`/org-unit/search`) goes to `unitId` of the API */}
      <Controller
        name="unitId"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={unitOptions || []}
            disabled={disabled}
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                required
                label={t('pages.candidates.candidateCreation.unit')}
                placeholder={t('pages.requests.creationModal.selectUnit')}
                error={!!errors?.unitId?.message}
                helperText={errors?.unitId?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="divisionId"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={divisionOptions || []}
            disabled={disabled}
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                required
                label={t('pages.candidates.candidateCreation.division')}
                placeholder={t('pages.requests.creationModal.selectDivision')}
                error={!!errors?.divisionId?.message}
                helperText={errors?.divisionId?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="priority"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={priorityOptions || []}
            renderInput={(params) => (
              <Input
                {...params}
                required
                label={t('pages.candidates.allCandidatesTable.priority')}
                placeholder={t('pages.requests.creationModal.selectPriority')}
                error={!!errors?.priority?.message}
                helperText={errors?.priority?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="deadline"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Styled.DatePickerContainer>
            <DatePicker
              selected={value ? new Date(value) : null}
              onChange={onChange}
              error={!!errors?.deadline?.message}
              isConstrained={false}
              helperText={errors?.deadline?.message?.toString()}
              label={t('pages.requests.creationModal.deadline')}
              placeholder={t('pages.requests.creationModal.date')}
              minDate={new Date()}
            />
          </Styled.DatePickerContainer>
        )}
      />
      <Controller
        name="professions"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={professionOptions || []}
            multiple
            limitTags={1}
            disableCloseOnSelect
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                required
                label={t('pages.vacancies.creationModal.specialty')}
                placeholder={t('pages.vacancies.creationModal.selectSpecialty')}
                error={!!errors?.professions?.message}
                helperText={errors?.professions?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="domains"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Select
            value={value}
            onChange={onChange}
            options={domainOptions || []}
            multiple
            limitTags={1}
            disableCloseOnSelect
            disableSortByAlphabet={false}
            renderInput={(params) => (
              <Input
                {...params}
                label={t('pages.vacancies.creationModal.domain')}
                placeholder={t('pages.vacancies.creationModal.selectDomain')}
                error={!!errors?.domains?.message}
                helperText={errors?.domains?.message?.toString()}
              />
            )}
          />
        )}
      />
      <Controller
        name="requiredCount"
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Styled.FullNumberInput
            label={t('pages.requests.creationModal.fullCount')}
            required
            // US-10.3 AC-4: not less than the candidates already found
            min={
              isEdit && currentRequestEdition ? Math.max(getRequestFoundCandidatesCount(currentRequestEdition), 1) : 1
            }
            max={50}
            value={value}
            onChange={onChange}
            error={!!errors?.requiredCount?.message}
            helperText={errors?.requiredCount?.message?.toString()}
            onStep={onChange}
          />
        )}
      />
      <Styled.CheckboxGroup>
        <Controller
          name="forPreoffer"
          control={control}
          render={({ field: { onChange, value } }) => (
            <LabeledCheckbox
              disabled={disabled}
              onChange={onChange}
              checked={value}
              label={t('pages.requests.creationModal.preoffer')}
            />
          )}
        />
        <Controller
          name="forProject"
          control={control}
          render={({ field: { onChange, value } }) => (
            <LabeledCheckbox
              disabled={disabled}
              onChange={onChange}
              checked={value}
              label={t('pages.requests.viewModal.onProject')}
            />
          )}
        />
      </Styled.CheckboxGroup>
    </FormSection>
  );
};

export default GeneralSection;
